import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  CircularProgress,
  Dialog,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import ImageIcon from '@material-ui/icons/Image';
import MicIcon from '@material-ui/icons/Mic';
import DescriptionIcon from '@material-ui/icons/Description';
import VideocamIcon from '@material-ui/icons/Videocam';
import { format, parseISO } from 'date-fns';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    height: '75vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    borderBottom: '1px solid #e8e8e8',
    flexShrink: 0,
    background: '#fff',
  },
  avatar: {
    width: 36,
    height: 36,
    backgroundColor: '#3f51b5',
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    flexShrink: 0,
  },
  contactName: {
    fontWeight: 700,
    fontSize: '0.92rem',
    color: '#172B4D',
    lineHeight: 1.2,
  },
  ticketNum: {
    fontSize: '0.72rem',
    color: '#6B778C',
  },
  headerActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    background: '#ECE5DD',
  },
  bubble: {
    maxWidth: '78%',
    padding: '7px 11px',
    borderRadius: 10,
    fontSize: '0.84rem',
    lineHeight: 1.45,
    wordBreak: 'break-word',
    position: 'relative',
    boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
  },
  bubbleMe: {
    alignSelf: 'flex-end',
    backgroundColor: '#DCF8C6',
    color: '#111',
    borderTopRightRadius: 2,
  },
  bubbleThem: {
    alignSelf: 'flex-start',
    backgroundColor: '#fff',
    color: '#111',
    borderTopLeftRadius: 2,
  },
  bubbleTime: {
    fontSize: '0.65rem',
    marginTop: 3,
    textAlign: 'right',
    color: '#6B778C',
  },
  senderName: {
    fontSize: '0.72rem',
    fontWeight: 700,
    marginBottom: 2,
    color: '#00875A',
  },
  mediaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#555',
    fontSize: '0.82rem',
    fontStyle: 'italic',
  },
  inputArea: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 10px',
    borderTop: '1px solid #e0e0e0',
    background: '#F0F0F0',
    gap: 8,
    flexShrink: 0,
  },
  inputBase: {
    flex: 1,
    background: '#fff',
    borderRadius: 20,
    padding: '6px 14px',
    fontSize: '0.88rem',
    border: '1px solid #ddd',
    '& input': { padding: 0 },
  },
  sendBtn: {
    color: '#00875A',
    '&:hover': { color: '#006644' },
  },
  goToBtn: {
    color: '#1976d2',
    padding: 6,
  },
  closeBtn: {
    color: '#6B778C',
    padding: 6,
  },
  loadingBox: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#ECE5DD',
  },
  emptyMsg: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6B778C',
    fontSize: '0.85rem',
    background: '#ECE5DD',
  },
}));

const MEDIA_ICONS = {
  image: { icon: <ImageIcon style={{ fontSize: 16 }} />, label: 'Imagem' },
  video: { icon: <VideocamIcon style={{ fontSize: 16 }} />, label: 'V\u00eddeo' },
  audio: { icon: <MicIcon style={{ fontSize: 16 }} />, label: '\u00c1udio' },
  ptt: { icon: <MicIcon style={{ fontSize: 16 }} />, label: 'Voz' },
  document: { icon: <DescriptionIcon style={{ fontSize: 16 }} />, label: 'Documento' },
};

export default function KanbanConversationDialog({ open, onClose, ticket }) {
  const classes = useStyles();
  const history = useHistory();
  const bottomRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !ticket) return;
    setLoading(true);
    setMessages([]);
    api.get(`/messages/${ticket.id}`, { params: { pageNumber: 1 } })
      .then(({ data }) => setMessages(data.messages || []))
      .catch(err => console.error('KanbanDialog:', err))
      .finally(() => setLoading(false));
  }, [open, ticket]);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [loading, messages.length]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    const body = text.trim();
    setText('');
    setSending(true);
    try {
      const { data } = await api.post(`/messages/${ticket.id}`, { body, fromMe: true });
      setMessages(prev => [...prev, data]);
    } catch (err) {
      console.error('Erro ao enviar:', err);
      setText(body);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleGoToTicket = () => {
    onClose();
    history.push(`/tickets/${ticket.uuid}`);
  };

  const getInitials = (name = '') =>
    name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join('');

  const renderMessage = (msg) => {
    const mediaInfo = MEDIA_ICONS[msg.mediaType];
    const hasBody = msg.body && msg.body.trim() && msg.body !== 'undefined';

    return (
      <div key={msg.id} className={`${classes.bubble} ${msg.fromMe ? classes.bubbleMe : classes.bubbleThem}`}>
        {!msg.fromMe && msg.contact?.name && (
          <div className={classes.senderName}>{msg.contact.name}</div>
        )}
        {mediaInfo && (
          <div className={classes.mediaRow}>
            {mediaInfo.icon}
            <span>{mediaInfo.label}</span>
          </div>
        )}
        {hasBody && <div style={{ whiteSpace: 'pre-wrap' }}>{msg.body}</div>}
        <div className={classes.bubbleTime}>
          {msg.createdAt ? format(parseISO(msg.createdAt), 'HH:mm') : ''}
        </div>
      </div>
    );
  };

  if (!ticket) return null;

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }} maxWidth={false}>
      {/* Header */}
      <div className={classes.header}>
        <Avatar src={ticket.contact?.urlPicture} className={classes.avatar}>
          {!ticket.contact?.urlPicture && getInitials(ticket.contact?.name)}
        </Avatar>
        <div style={{ minWidth: 0 }}>
          <Typography className={classes.contactName} noWrap>
            {ticket.contact?.name}
          </Typography>
          <Typography className={classes.ticketNum}>
            #{ticket.id} &bull; {ticket.queue?.name || 'Sem fila'}
          </Typography>
        </div>
        <div className={classes.headerActions}>
          <Tooltip title="Abrir em Atendimentos">
            <IconButton size="small" className={classes.goToBtn} onClick={handleGoToTicket}>
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" className={classes.closeBtn} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {/* Messages */}
      {loading ? (
        <div className={classes.loadingBox}><CircularProgress size={28} /></div>
      ) : messages.length === 0 ? (
        <div className={classes.emptyMsg}>Nenhuma mensagem ainda</div>
      ) : (
        <div className={classes.messagesContainer}>
          {messages.map(renderMessage)}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Input */}
      <div className={classes.inputArea}>
        <InputBase
          className={classes.inputBase}
          placeholder="Digite uma mensagem..."
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          maxRows={3}
          disabled={sending}
        />
        <Tooltip title="Para \u00e1udio e arquivos, use Atendimentos \u2192">
          <span>
            <IconButton size="small" onClick={handleSend} disabled={!text.trim() || sending} className={classes.sendBtn}>
              <SendIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </Dialog>
  );
}
